<template>
  <div class="pv-page">
    <q-card flat bordered class="pv-card pv-card--filters">
      <q-card-section class="pv-card__toolbar row items-center no-wrap">
        <div>
          <div class="pv-card__toolbar-title">Параметры</div>
          <div class="pv-card__toolbar-hint text-grey-7">
            Учитываются только накладные, отправленные в 1С. Суммы считаются на кол-во из накладной.
          </div>
        </div>
      </q-card-section>
      <q-separator class="pv-sep" />
      <q-card-section class="pv-filter-body">
        <div class="pv-filter-grid">
          <div class="pv-filter-row">
            <CustomDate
              label="Период с"
              :value="filters.date_from"
              :width="140"
              @change_date="(v) => onDateFrom(v)"
            />
            <CustomDate
              label="по"
              :value="filters.date_to"
              :width="140"
              @change_date="(v) => onDateTo(v)"
            />
            <CustomSelect
              class="pv-field pv-field--grow"
              :options="biznesRegions"
              label="Бизнес регион"
              option-value-key="biznes_region__id"
              option-label-key="biznes_region_name"
              :clearable="true"
              :value="filters.biznes_region__id"
              @change="(v) => onFilterChange('biznes_region__id', v)"
            />
            <CustomSelect
              class="pv-field pv-field--grow"
              :options="sbsProjects"
              label="Проект"
              option-value-key="sbs_project_id"
              option-label-key="sbs_project_name"
              :clearable="true"
              :value="filters.sbs_project_id"
              @change="(v) => onFilterChange('sbs_project_id', v)"
            />
          </div>
          <div class="pv-filter-row">
            <CustomSelect
              class="pv-field pv-field--grow"
              :options="providers"
              label="Поставщик"
              option-value-key="provider_id"
              option-label-key="provider_name"
              :clearable="true"
              :value="filters.provider_id"
              @change="(v) => onFilterChange('provider_id', v)"
            />
            <CustomSelect
              class="pv-field pv-field--grow"
              :options="agreements"
              label="Договор"
              option-value-key="contractor_agreement_list_id"
              option-label-key="agreement_label"
              :clearable="true"
              :value="filters.contractor_agreement_list_id"
              @change="(v) => onFilterChange('contractor_agreement_list_id', v)"
            />
            <div class="pv-field pv-field--material">
              <div class="pv-field__label">Номенклатура</div>
              <div class="pv-material-picker row items-center no-wrap q-gutter-xs">
                <q-input
                  dense
                  outlined
                  readonly
                  class="pv-material-picker__input col"
                  :model-value="selectedMaterialLabel"
                  placeholder="Все"
                />
                <q-btn
                  flat
                  dense
                  color="primary"
                  label="Выбрать"
                  @click="openMaterialModal"
                />
                <q-btn
                  v-if="filters.material_id"
                  flat
                  dense
                  round
                  icon="close"
                  color="grey-7"
                  @click="clearMaterial"
                />
              </div>
            </div>
            <CustomInput
              class="pv-field"
              :value="filters.invoice_num"
              placeholder="Номер накладной"
              label="Номер накл-д"
              v-debounce:300="onInvoiceNumChange"
            />
          </div>
          <div class="pv-filter-row pv-filter-row--bottom">
            <CustomSelect
              class="pv-field"
              :options="deviationOptions"
              label="Отклонения"
              option-value-key="value"
              option-label-key="label"
              :value="filters.deviation_type"
              @change="onDeviationType"
            />
            <div class="pv-toggle-wrap column justify-end">
              <q-toggle
                v-model="filters.only_positive_diff"
                label="Только факт > индикатив"
                dense
                color="primary"
                @update:model-value="onPositiveDiffToggle"
              />
            </div>
            <div class="pv-filter-actions row items-center q-gutter-sm">
              <q-btn
                unelevated
                color="primary"
                icon="search"
                label="Показать"
                :loading="loading"
                @click="fetchReport"
              />
              <q-btn
                flat
                color="grey-7"
                icon="clear"
                label="Очистить"
                @click="clearFilters"
              />
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>

    <div v-if="rows.length" class="pv-totals-bar q-mt-sm row items-center justify-between">
      <div class="pv-total-block">
        <div class="pv-total-label">Строк</div>
        <div class="pv-total-value">{{ rows.length }}</div>
      </div>
      <q-btn
        unelevated
        dense
        color="positive"
        icon="download"
        label="Excel"
        :loading="exporting"
        class="pv-export-btn"
        @click="exportExcel"
      />
    </div>

    <q-banner v-if="errorText" rounded class="pv-banner q-mt-md">
      <div class="pv-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="pv-progress q-mt-md"
      rounded
      size="4px"
    />

    <q-card v-if="rows.length" flat bordered class="pv-card pv-card--table q-mt-md">
      <div class="pv-table-scroll">
        <table class="pv-table" cellspacing="0">
          <colgroup>
            <col class="pv-col-region" />
            <col class="pv-col-project" />
            <col class="pv-col-provider" />
            <col class="pv-col-material" />
            <col class="pv-col-unit" />
            <col class="pv-col-qty" />
            <col class="pv-col-price" span="3" />
            <col class="pv-col-sum" span="3" />
            <col class="pv-col-pct" />
            <col class="pv-col-invoice" />
            <col class="pv-col-agreement" />
          </colgroup>
          <thead class="pv-thead">
            <tr class="pv-tr pv-tr--head-top">
              <th class="pv-th pv-th--sticky-left" rowspan="2">Бизнес регион</th>
              <th class="pv-th" rowspan="2">Проект</th>
              <th class="pv-th pv-th--provider" rowspan="2">Поставщик</th>
              <th class="pv-th pv-th--name" rowspan="2">Номенклатура</th>
              <th class="pv-th" rowspan="2">Ед. изм.</th>
              <th class="pv-th pv-th--num" rowspan="2">Кол-во</th>
              <th class="pv-th pv-th--group pv-th--divider-right" colspan="3">Цена</th>
              <th class="pv-th pv-th--group pv-th--divider-right" colspan="3">Сумма</th>
              <th class="pv-th pv-th--num pv-th--divider-right" rowspan="2">%</th>
              <th class="pv-th pv-th--center pv-th--divider-right pv-th--invoice" rowspan="2">Накладная</th>
              <th class="pv-th" rowspan="2">Договор</th>
            </tr>
            <tr class="pv-tr pv-tr--head-sub">
              <th class="pv-th pv-th--num pv-th--sub">Факт</th>
              <th class="pv-th pv-th--num pv-th--sub">Индикатив</th>
              <th class="pv-th pv-th--num pv-th--sub pv-th--divider-right">Разница</th>
              <th class="pv-th pv-th--num pv-th--sub">Факт</th>
              <th class="pv-th pv-th--num pv-th--sub">Индикатив</th>
              <th class="pv-th pv-th--num pv-th--sub pv-th--divider-right">Разница</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.provider_request_invoice_item_id"
              class="pv-tr pv-tr--data"
              :class="{ 'pv-tr--exceed': Number(row.price_diff) > 0 }"
            >
              <td class="pv-td pv-td--sticky-left">{{ row.biznes_region_name || '—' }}</td>
              <td class="pv-td">{{ formatProjectName(row) }}</td>
              <td class="pv-td pv-td--provider">
                <span
                  v-if="row.provider_name"
                  class="pv-cell-ellipsis"
                  :title="row.provider_name"
                >{{ row.provider_name }}</span>
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--name">
                <span v-if="row.material_name" :title="row.material_name">{{ row.material_name }}</span>
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td">{{ row.unit_name || '—' }}</td>
              <td class="pv-td pv-td--num">
                <Amount v-if="row.material_cnt != null" :value="row.material_cnt" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num">
                <Amount v-if="row.fact_price != null" :value="row.fact_price" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num">
                <Amount v-if="row.indicative_price != null" :value="row.indicative_price" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num pv-td--diff">
                <Amount v-if="row.price_diff != null" :value="row.price_diff" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num">
                <Amount v-if="row.fact_sum != null" :value="row.fact_sum" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num">
                <Amount v-if="row.indicative_sum != null" :value="row.indicative_sum" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num pv-td--diff">
                <Amount v-if="row.sum_diff != null" :value="row.sum_diff" round="2" />
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--num">
                <span v-if="row.diff_percent != null">{{ formatPercent(row.diff_percent) }}</span>
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td pv-td--stacked pv-td--invoice">
                <template v-if="row.invoice_num || row.kod_bk || row.invoice_date">
                  <div
                    v-if="row.invoice_num"
                    class="pv-stack__primary pv-cell-ellipsis"
                    :title="row.invoice_num"
                  >
                    {{ row.invoice_num }}
                  </div>
                  <div
                    v-if="row.kod_bk"
                    class="pv-stack__kod pv-cell-ellipsis"
                    :title="row.kod_bk"
                  >
                    ({{ row.kod_bk }})
                  </div>
                  <div v-if="row.invoice_date" class="pv-stack__secondary">
                    {{ formatInvoiceDate(row.invoice_date) }}
                  </div>
                </template>
                <span v-else class="pv-dash">—</span>
              </td>
              <td class="pv-td">{{ row.agreement_num || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </q-card>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="pv-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <NotFoundText />
      </q-card-section>
    </q-card>

    <q-card
      v-else-if="!loading && !searched"
      flat
      bordered
      class="pv-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl text-grey-7">
        Укажите период и нажмите «Показать»
      </q-card-section>
    </q-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import Amount from '@/components/common/Amount.vue';
import NotFoundText from '@/components/common/NotFoundText.vue';
import CustomDate from '@/components/form/CustomDate';
import CustomInput from '@/components/form/CustomInput';
import CustomSelect from '@/components/form/CustomSelect';
import { MODAL_NAMES_AIDAR } from '@/constants/index_aidar';
import { toValidComponentDate } from '@/_helpers';
import { useAppStore } from '@/services/store/app';
import { getContractorAgreementList } from '@/views/ContractorAgreementList/services';
import { getProviders } from '@/views/Materials/services';
import {
  exportPriceVarianceReport,
  getPriceVarianceFilters,
  getPriceVarianceReport,
} from '@/views/ResidentialSettings/services';

const appStore = useAppStore();

const deviationOptions = [
  { value: 'all', label: 'Все' },
  { value: 'exceed', label: 'Превышение' },
  { value: 'under', label: 'Занижение' },
];

function defaultDateFrom() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function defaultDateTo() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const biznesRegions = ref([]);
const sbsProjects = ref([]);
const providers = ref([]);
const agreements = ref([]);
const selectedMaterial = ref(null);

const filters = ref({
  date_from: defaultDateFrom(),
  date_to: defaultDateTo(),
  biznes_region__id: '',
  sbs_project_id: '',
  provider_id: '',
  contractor_agreement_list_id: '',
  material_id: '',
  invoice_num: '',
  deviation_type: 'all',
  only_positive_diff: false,
});

const rows = ref([]);
const loading = ref(false);
const exporting = ref(false);
const searched = ref(false);
const errorText = ref('');

const selectedMaterialLabel = computed(() => {
  if (!selectedMaterial.value) return '';
  return selectedMaterial.value.material_name || `ID ${selectedMaterial.value.material_id}`;
});

function formatPercent(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
}

function formatProjectName(row) {
  const name = row?.sbs_project_name;
  if (!name) return '—';
  const id = row?.project_id;
  if (id != null && id !== '') return `${name} (${id})`;
  return name;
}

function formatInvoiceDate(v) {
  if (!v) return '—';
  const d = new Date(toValidComponentDate(v, true));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU');
}

function isPeriodValid() {
  const from = filters.value.date_from;
  const to = filters.value.date_to;
  if (!from || !to) return false;
  const fromIso = toValidComponentDate(from, true);
  const toIso = toValidComponentDate(to, true);
  if (!fromIso || !toIso) return true;
  return fromIso <= toIso;
}

function buildParams() {
  const p = {
    date_from: filters.value.date_from,
    date_to: filters.value.date_to,
    deviation_type: filters.value.deviation_type || 'all',
    only_positive_diff: filters.value.only_positive_diff ? 1 : 0,
  };
  if (filters.value.biznes_region__id) p.biznes_region__id = filters.value.biznes_region__id;
  if (filters.value.sbs_project_id) p.sbs_project_id = filters.value.sbs_project_id;
  if (filters.value.provider_id) p.provider_id = filters.value.provider_id;
  if (filters.value.contractor_agreement_list_id) {
    p.contractor_agreement_list_id = filters.value.contractor_agreement_list_id;
  }
  if (filters.value.material_id) p.material_id = filters.value.material_id;
  if (filters.value.invoice_num?.trim()) p.invoice_num = filters.value.invoice_num.trim();
  return p;
}

function onFilterChange(key, value) {
  filters.value[key] = value || '';
}

function onInvoiceNumChange(v) {
  filters.value.invoice_num = v || '';
}

function onDateFrom(v) {
  filters.value.date_from = v || '';
}

function onDateTo(v) {
  filters.value.date_to = v || '';
}

function onDeviationType(v) {
  filters.value.deviation_type = v || 'all';
  if (filters.value.only_positive_diff && filters.value.deviation_type === 'under') {
    filters.value.deviation_type = 'exceed';
  }
}

function onPositiveDiffToggle(v) {
  if (v && filters.value.deviation_type === 'under') {
    filters.value.deviation_type = 'exceed';
  }
}

function openMaterialModal() {
  appStore.showModal(MODAL_NAMES_AIDAR.SelectMaterial, {
    title: 'Выберите номенклатуру',
    material_id: filters.value.material_id,
    onSelect: (material) => {
      if (!material) return;
      selectedMaterial.value = material;
      filters.value.material_id = material.material_id;
    },
  });
}

function clearMaterial() {
  selectedMaterial.value = null;
  filters.value.material_id = '';
}

function clearFilters() {
  filters.value = {
    date_from: defaultDateFrom(),
    date_to: defaultDateTo(),
    biznes_region__id: '',
    sbs_project_id: '',
    provider_id: '',
    contractor_agreement_list_id: '',
    material_id: '',
    invoice_num: '',
    deviation_type: 'all',
    only_positive_diff: false,
  };
  selectedMaterial.value = null;
  rows.value = [];
  searched.value = false;
  errorText.value = '';
}

async function fetchReport() {
  if (!filters.value.date_from || !filters.value.date_to) {
    errorText.value = 'Укажите период';
    return;
  }
  if (!isPeriodValid()) {
    errorText.value = 'Дата начала не может быть позже даты окончания';
    return;
  }
  errorText.value = '';
  loading.value = true;
  searched.value = true;
  const res = await getPriceVarianceReport(buildParams());
  loading.value = false;
  if (!res) {
    rows.value = [];
    if (!errorText.value) errorText.value = 'Ошибка загрузки отчёта';
    return;
  }
  rows.value = Array.isArray(res) ? res : [];
}

async function exportExcel() {
  if (!filters.value.date_from || !filters.value.date_to) return;
  if (!isPeriodValid()) {
    errorText.value = 'Дата начала не может быть позже даты окончания';
    return;
  }
  exporting.value = true;
  errorText.value = '';
  const blob = await exportPriceVarianceReport(buildParams());
  exporting.value = false;
  if (!blob) {
    errorText.value = 'Ошибка экспорта';
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `price_variance_${filters.value.date_from}_${filters.value.date_to}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

async function loadFilterOptions() {
  const [filterOptions, providersList, agreementList] = await Promise.all([
    getPriceVarianceFilters(),
    getProviders({}),
    getContractorAgreementList(),
  ]);

  biznesRegions.value = filterOptions?.biznesRegions || [];
  sbsProjects.value = filterOptions?.sbsProjects || [];

  const providerRows = providersList?.data ?? providersList ?? [];
  providers.value = Array.isArray(providerRows) ? providerRows : [];

  agreements.value = (Array.isArray(agreementList) ? agreementList : []).map((a) => ({
    ...a,
    agreement_label: a.agreement_num
      ? `${a.agreement_num}${a.sbs_project_name ? ` (${a.sbs_project_name})` : ''}`
      : String(a.contractor_agreement_list_id),
  }));

}

onMounted(() => {
  loadFilterOptions();
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/quasar.variables.scss' as *;

.pv-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.pv-card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid $gray-light;
  background: #fff;
}

.pv-card__toolbar {
  padding: 12px 16px;
}

.pv-card__toolbar-hint {
  font-size: 12px;
  margin-top: 2px;
}

.pv-card__toolbar-title {
  font-size: 15px;
  font-weight: 600;
  color: $primary;
}

.pv-filter-body {
  padding: 12px 16px 16px;
}

.pv-filter-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pv-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.pv-filter-row--bottom {
  align-items: flex-end;
}

.pv-field {
  min-width: 160px;
}

.pv-field--grow {
  flex: 1;
  min-width: 180px;
}

.pv-field--material {
  flex: 1;
  min-width: 220px;
}

.pv-field__label {
  font-size: 12px;
  color: $gray;
  margin-bottom: 4px;
}

.pv-material-picker__input {
  min-width: 140px;
}

.pv-toggle-wrap {
  min-height: 40px;
  padding-bottom: 2px;
}

.pv-filter-actions {
  margin-left: auto;
}

.pv-totals-bar {
  padding: 0 4px;
}

.pv-total-label {
  font-size: 12px;
  color: $gray;
}

.pv-total-value {
  font-size: 18px;
  font-weight: 600;
}

.pv-banner {
  background: #fff3e0;
}

.pv-table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  max-width: 100%;
}

.pv-table {
  width: 100%;
  min-width: 1280px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.pv-col-region { width: 120px; }
.pv-col-project { width: 160px; }
.pv-col-provider { width: 150px; }
.pv-col-material { width: 360px; }
.pv-col-unit { width: 64px; }
.pv-col-qty { width: 72px; }
.pv-col-price { width: 88px; }
.pv-col-sum { width: 96px; }
.pv-col-pct { width: 56px; }
.pv-col-invoice { width: 108px; }
.pv-col-agreement { width: 120px; }

.pv-thead {
  background: $primary;
  color: #fff;
}

.pv-th {
  padding: 8px 10px;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
}

.pv-th--num,
.pv-th--center {
  text-align: center;
}

.pv-th--sub {
  font-size: 12px;
  font-weight: 500;
}

.pv-th--provider {
  width: 150px;
}

.pv-th--name {
  width: 360px;
}

.pv-th--invoice {
  width: 108px;
}

.pv-th--sticky-left,
.pv-td--sticky-left {
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
}

.pv-td--sticky-left {
  background: #fff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
}

.pv-tr--data:nth-child(even) .pv-td--sticky-left {
  background: #fafafa;
}

.pv-td {
  padding: 6px 10px;
  border-bottom: 1px solid $gray-light;
  vertical-align: middle;
}

.pv-td--num {
  text-align: right;
  white-space: nowrap;
}

.pv-td--center {
  text-align: center;
}

.pv-td--stacked {
  line-height: 1.35;
  vertical-align: top;
}

.pv-td--invoice {
  max-width: 108px;
  padding-left: 6px;
  padding-right: 6px;
  font-size: 12px;
}

.pv-cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.pv-stack__primary {
  font-weight: 500;
}

.pv-stack__kod {
  font-size: 12px;
  color: $gray;
  margin-top: 2px;
}

.pv-stack__secondary {
  font-size: 12px;
  color: $gray;
  margin-top: 2px;
  white-space: nowrap;
}

.pv-td--provider {
  max-width: 150px;
  vertical-align: top;
}

.pv-td--name {
  vertical-align: top;
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

.pv-td--diff {
  color: #c62828;
  font-weight: 500;
}

.pv-tr--exceed .pv-td--diff {
  color: #c62828;
}

.pv-tr--data:hover {
  background: #f5f9ff;
}

.pv-dash {
  color: $gray;
}

.pv-empty {
  border-radius: 8px;
}
</style>
